// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2, Trophy, Wand2, GripVertical, ListOrdered, Link2, Unlink, ArrowLeftRight, Plus, Trash2, Pencil, Check, X,
  ChevronDown, MoreHorizontal, MoveVertical,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  pointerWithin,
} from '@dnd-kit/core';
import { endOfRoundWords, gameWords, moveBlocker, planBracketMove } from '@/lib/bracketMove';
import { formatCalendarDate, formatTime } from "@/lib/dates";
import {
  computeBracketLayout,
  fitToScreen,
  ROW_H as LAYOUT_ROW_H,
  ROW_PITCH,
  MATCHUP_H as LAYOUT_MATCHUP_H,
  type BracketGameInput,
} from './bracketLayout';
import { gameName } from "@/lib/gameNumber";
import { displayRoundName, planRoundRename, renamePlanSentence } from "@/lib/roundName";
import { slotOptions, crossBracketGroups } from "@/lib/slotOptions";
import SwapSeedsDialog from '@/components/admin/schedule/SwapSeedsDialog';
import InlineNote from '@/components/admin/schedule/InlineNote';

const GENERATE_FN = 'rle-generate-bracket';
const MANUAL_FN = 'rle-operator-schedule-manual';
/** His own words, capped so they stay readable on a bracket line. */
export const GAME_NOTE_LIMIT = 120;
export const ROUND_NOTE_LIMIT = 120;
export const BRACKET_DESCRIPTION_LIMIT = 500;

interface BracketViewProps {
  eventId: string;
  canManage: boolean;
  /** Draw a specific schedule version (the operator's working version) instead
   *  of resolving the published/newest one. */
  versionId?: string | null;
  /** Controlled division. When given, the internal division chips are hidden. */
  division?: string | null;
  /** Draw one named bracket inside the division — Gold, Silver, Placement.
   *  Without it, every bracket game in the division is drawn together. */
  bracketId?: string | null;
  /** The bracket's name, for the "add a game" wording. */
  bracketName?: string | null;
  /** Hide the built-in generate/manual-seed buttons (the operator Brackets
   *  panel owns that flow). */
  hideActions?: boolean;
  /** Draw the bracket the way a parent sees it: no editing, nothing clickable. */
  preview?: boolean;
  /** Bump to force a re-read of v_bracket_games. */
  refreshToken?: number;
  /** Edit bracket shape and seeded team slots in place. Placement is shown
   *  read-only and is changed on the Grid. */
  editable?: boolean;
  /** Called after a successful in-place edit or seed exchange. */
  onChanged?: () => void;
}

const fmtTime = (t?: string | null) => formatTime(t) || null;

/** The column header and the card agree: a round is always named by its
 *  number, with the familiar word added when the bracket is big enough for
 *  that word to mean anything. A one-game bracket is "Round 1", never "Final". */
const roundLabel = (round: number, maxRound: number) => {
  if (maxRound <= 1) return `Round ${round}`;
  const from = maxRound - round;
  if (from === 0) return `Round ${round} · Final`;
  if (from === 1) return `Round ${round} · Semifinals`;
  if (from === 2) return `Round ${round} · Quarterfinals`;
  return `Round ${round}`;
};

const TBD = '__tbd__';

const SOURCE_LABELS: Record<string, string> = {
  team: 'Team',
  winner_of: 'Winner of',
  loser_of: 'Loser of',
  division_place: 'Division place',
  pool_place: 'Pool place',
};

const EDIT_SOURCE_LABELS: Record<string, string> = {
  unset: 'Not set',
  ...SOURCE_LABELS,
};

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
};

/**
 * The bracket is drawn the way a bracket is drawn on paper: the line IS the
 * bracket. A team is a row with a bottom border, two rows are joined by one
 * vertical on the right, and a stub runs from that vertical's midpoint into
 * the next round's team line. No SVG, no canvas, no path maths — every line
 * is a border on a positioned element, so nothing has to be kept in sync.
 *
 * Editing lives in a side panel: clicking a game opens the slot sources, the
 * game's name, Take off the board and delete. The canvas itself stays a clean
 * line diagram, which is also the public preview for free.
 */
// The geometry now lives in bracketLayout.ts, a pure function with its own
// tests. Nothing here recomputes it; these are only the names the drawing uses.
const ROW_H = LAYOUT_ROW_H;        // a team's row, text sitting on its line
const MATCHUP_GAP = ROW_PITCH;     // distance between the two team rows
const MATCHUP_H = LAYOUT_MATCHUP_H;// the whole unit, fixed in every round
const COL_W_FALLBACK = 236;        // only when a matchup is drawn outside the canvas
const ROUND_HEADER_H = 48;         // the column-label strip above the bracket canvas

const findBracketScrollPane = (el: HTMLElement): HTMLElement => {
  let node = el.parentElement;
  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.clientHeight > 0) return node;
    node = node.parentElement;
  }
  return el;
};


/**
 * One slot, one list, led by what the slot actually is. A game may only be fed
 * by a game in a LOWER bracket_round — that one comparison is both the
 * relevance filter and the loop guard, since nothing in the same or a later
 * round can ever be offered. A first-round slot therefore leads with teams; a
 * later slot leads with the round immediately before it, winners by default and
 * losers on a consolation game. Everything else sits behind a link.
 */
const SlotPicker: React.FC<{
  g: any;
  side: 'a' | 'b';
  disabled?: boolean;
  teams: Array<{ id: string; name: string }>;
  bracketGames: any[];
  brackets?: Array<{ id: string; name: string }>;
  /** The division these brackets belong to, so a heading reads "16u Winners". */
  division?: string | null;
  onSetSlot?: (gameId: string, side: 'a' | 'b', type: string, ref: any) => Promise<boolean>;
  children: React.ReactNode;
}> = ({ g, side, disabled, teams, bracketGames, brackets = [], division, onSetSlot, children }) => {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showTeams, setShowTeams] = useState(false);
  // One rule, one function — the same one the tests measure. It applies inside
  // this bracket only; another bracket's rounds are its own business.
  const mine = useMemo(
    () => (bracketGames ?? []).filter((x: any) =>
      !g?.bracket_id || !x.bracket_id || String(x.bracket_id) === String(g.bracket_id)),
    [bracketGames, g],
  );
  const { earlier, prev, prevName, losersFirst, hasEarlier, moreThanPrev } =
    useMemo(() => slotOptions(g, mine), [g, mine]);
  const others = useMemo(
    () => crossBracketGroups(g, bracketGames ?? [], brackets),
    [g, bracketGames, brackets],
  );

  const pick = (type: string, ref: any) => {
    setOpen(false);
    setShowAll(false);
    setShowTeams(false);
    void onSetSlot?.(g.id, side, type, ref);
  };
  const feedItems = (list: any[], type: 'winner_of' | 'loser_of', keyPrefix = '') =>
    list.map((x: any) => (
      <CommandItem
        key={`${keyPrefix}${type}-${x.id}`}
        value={`${keyPrefix}${type === 'winner_of' ? 'winner' : 'loser'} ${x.label}`}
        onSelect={() => pick(type, { game_id: x.id })}
      >
        {type === 'winner_of' ? 'Winner of' : 'Loser of'} {x.label}
      </CommandItem>
    ));
  const teamItems = teams.map((t) => (
    <CommandItem key={`t-${t.id}`} value={`team ${t.name}`} onSelect={() => pick('team', { registration_id: t.id })}>
      {t.name}
    </CommandItem>
  ));

  return (
    <Popover
      open={open}
      onOpenChange={(v) => { setOpen(v); if (!v) { setShowAll(false); setShowTeams(false); } }}
    >
      <PopoverTrigger asChild disabled={disabled}>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] p-0" onClick={(e) => e.stopPropagation()}>
        <Command>
          <CommandInput placeholder="Type to filter…" />
          <CommandList className="max-h-[320px]">
            <CommandEmpty>Nothing matches.</CommandEmpty>

            {/* What this slot actually takes, first. */}
            {!hasEarlier && !others.length && <CommandGroup heading="Teams">{teamItems}</CommandGroup>}
            {hasEarlier && (
              <CommandGroup heading={`${losersFirst ? 'Loser' : 'Winner'} of a ${prevName}`}>
                {feedItems(prev, losersFirst ? 'loser_of' : 'winner_of')}
              </CommandGroup>
            )}

            {/* Every other bracket in this division, under its own heading —
                he thinks bracket first, game second: "the 16u winners, loser of
                game 34". A consolation bracket takes losers, so those lead. */}
            {others.map((b) => (
              <CommandGroup
                key={`b-${b.id}`}
                data-bracket-heading={b.name}
                heading={[division, b.name].filter(Boolean).join(' ')}
              >
                {feedItems(b.games, 'loser_of', `${b.name} `)}
                {feedItems(b.games, 'winner_of', `${b.name} `)}
              </CommandGroup>
            ))}

            {/* Behind the links: still lower rounds only, inside this bracket. */}
            {hasEarlier && showAll && (
              <>
                <CommandGroup heading="Winner of an earlier game">
                  {feedItems(earlier, 'winner_of')}
                </CommandGroup>
                <CommandGroup heading="Loser of an earlier game">
                  {feedItems(earlier, 'loser_of')}
                </CommandGroup>
              </>
            )}
            {(hasEarlier || !!others.length) && showTeams && (
              <CommandGroup heading="Teams">{teamItems}</CommandGroup>
            )}


            <CommandSeparator />
            <CommandGroup>
              <CommandItem value="clear this slot" onSelect={() => pick('unset', null)}>
                Clear this slot
              </CommandItem>
              {hasEarlier && !showAll && (moreThanPrev || !losersFirst) && (
                <CommandItem
                  value="show all earlier rounds"
                  onSelect={() => setShowAll(true)}
                  className="text-muted-foreground"
                >
                  show all earlier rounds
                </CommandItem>
              )}
              {(hasEarlier || !!others.length) && !showTeams && (
                <CommandItem
                  value="or pick a team directly"
                  onSelect={() => setShowTeams(true)}
                  className="text-muted-foreground"
                >
                  or pick a team directly
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/** The details line that sits in the crook of the ⊐, and links to the Grid. */
const GameDetails: React.FC<{ g: any; eventId: string; courtName?: string | null; preview?: boolean }> = ({ g, eventId, courtName, preview }) => {
  const isPlaced = !!(g.scheduled_date && g.scheduled_start_time && g.court_id);
  const words = isPlaced
    ? `${gameName(g)} · ${formatCalendarDate(g.scheduled_date, 'EEE d MMM', g.scheduled_date)} · ${fmtTime(g.scheduled_start_time)} · ${courtName ?? 'Court'}`
    : `${gameName(g)} · Not scheduled`;
  // In preview there is nothing to click: a parent sees the words, not a link.
  if (preview) {
    return (
      <span className="relative block -translate-y-1/2 truncate text-right text-[11px] leading-tight text-muted-foreground" data-game-detail-line>
        {words}
      </span>
    );
  }
  return (
    <Link
      to={`/admin-dashboard/events/${eventId}/scheduling/grid${isPlaced ? `?game=${encodeURIComponent(g.id)}&date=${encodeURIComponent(String(g.scheduled_date).slice(0, 10))}` : `?game=${encodeURIComponent(g.id)}`}`}
      onClick={(e) => e.stopPropagation()}
      className="relative block -translate-y-1/2 truncate text-right text-[11px] leading-tight text-muted-foreground hover:text-foreground hover:underline"
      title="Open this game on the Grid"
      data-game-detail-line
    >
      {words}
    </Link>
  );
};

/**
 * Where one game may go. A game has three to five legitimate destinations, so
 * the main way to move it is a short list, not a precision drag. Every round in
 * the bracket is listed — the impossible ones disabled with the reason on the
 * line, because hiding them makes a director think the feature is broken.
 */
export type MoveMenu = {
  current: number;
  rounds: Array<{ round: number; label: string; current: boolean; reason: string | null }>;
  up: { disabled: boolean; reason: string | null };
  down: { disabled: boolean; reason: string | null };
};

const MoveToMenu: React.FC<{
  g: any;
  label: string;
  options: MoveMenu;
  onPick: (round: number) => void;
  onNudge: (dir: 'up' | 'down') => void;
}> = ({ label, options, onPick, onNudge }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        size="icon" variant="ghost" className="h-5 w-5"
        title={`Move ${label} to another round or position`}
        aria-label={`Move ${label}`}
        data-bracket-move-menu
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <MoveVertical className="h-3 w-3" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-[min(20rem,calc(100vw-2rem))]">
      <DropdownMenuLabel>Move {label} to</DropdownMenuLabel>
      <DropdownMenuSeparator />
      {options.rounds.map((r) => (
        <DropdownMenuItem
          key={r.round}
          data-bracket-move-round={r.round}
          data-bracket-move-disabled={r.current || r.reason ? 'true' : undefined}
          disabled={r.current || !!r.reason}
          onSelect={() => onPick(r.round)}
          className="flex-col items-start gap-0.5"
        >
          <span className="flex w-full items-center justify-between gap-2">
            <span>{r.label}</span>
            {r.current && <span className="text-xs text-muted-foreground">current</span>}
          </span>
          {!r.current && r.reason && (
            <span className="text-xs leading-snug text-muted-foreground whitespace-normal">{r.reason}</span>
          )}
        </DropdownMenuItem>
      ))}
      <DropdownMenuSeparator />
      {(['up', 'down'] as const).map((dir) => (
        <DropdownMenuItem
          key={dir}
          data-bracket-move-nudge={dir}
          disabled={options[dir].disabled}
          onSelect={() => onNudge(dir)}
          className="flex-col items-start gap-0.5"
        >
          <span>Move {dir}</span>
          {options[dir].disabled && options[dir].reason && (
            <span className="text-xs leading-snug text-muted-foreground whitespace-normal">{options[dir].reason}</span>
          )}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

/**
 * One matchup, drawn as a ⊐: two team rows on their own lines, a vertical
 * joining them on the right, a stub from the vertical's midpoint, and the
 * game's details in the crook. Every control sits on the drawing itself —
 * each team line is its own dropdown, the pencil renames the game in place,
 * and a ⋯ menu holds Take off the board and Delete game.
 */
const Matchup: React.FC<{
  g: any;
  eventId: string;
  courtName?: string | null;
  slotOf?: (gameId: string, side: 'a' | 'b') => any;
  style?: React.CSSProperties;
  editable?: boolean;
  saving?: boolean;
  teams?: Array<{ id: string; name: string }>;
  bracketGames?: any[];
  brackets?: Array<{ id: string; name: string }>;
  /** The division, so a cross-bracket heading reads "16u Winners". */
  division?: string | null;

  onSetSlot?: (gameId: string, side: 'a' | 'b', type: string, ref: any) => Promise<boolean>;
  onRename?: (gameId: string, name: string) => Promise<boolean>;
  onDelete?: (game: any) => void;
  onTakeOff?: (game: any) => void;
  onSaveNote?: (gameId: string, note: string) => Promise<boolean>;
  preview?: boolean;
  /** column width from the layout engine */
  w?: number;
  /** a placement game — 3rd place and the like. Styling only, never a filter. */
  placement?: boolean;
  /** this game may be moved — the menu and the arrow keys */
  canMove?: boolean;
  /** …and the pointer may drag it (off while Fit to screen is scaling) */
  canDrag?: boolean;
  onKeyMove?: (gameId: string, dir: 'up' | 'down' | 'left' | 'right') => void;
  /** the short list of where this game may go, built from the same rule */
  moveOptions?: (gameId: string) => MoveMenu;
  onMoveToRound?: (gameId: string, round: number) => void;
  /** it is the one being dragged right now */
  moving?: boolean;
}> = ({ g, eventId, courtName, slotOf, style, editable, saving, teams = [], bracketGames = [], brackets = [], division, onSetSlot, onRename, onDelete, onTakeOff, onSaveNote, preview, w, placement, canMove, canDrag, onKeyMove, moveOptions, onMoveToRound, moving }) => {
  // Keyed on winner_registration_id, never on score or status: no game in the
  // project has ever had a score or reached completed, and a bye has a winner
  // and never a score. The winner column is the result.
  const winnerId = g.winner_registration_id ?? null;
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  const isPlaced = !!(g.scheduled_date && g.scheduled_start_time && g.court_id);
  const canEdit = !preview && !!editable && !!onSetSlot;
  // The whole block is the drag surface — a 20px grip was below every pointer
  // target guideline. The team rows, the details line and the action buttons
  // stop the pointer event so their own controls keep working.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `bg:${g.id}`,
    disabled: !canDrag,
  });
  const stopDrag = { onPointerDown: (e: React.PointerEvent) => e.stopPropagation() };

  const row = (side: 'a' | 'b') => {
    const s = slotOf?.(g.id, side) ?? null;
    const name = side === 'a' ? g.team_a_name : g.team_b_name;
    const score = side === 'a' ? g.score_a : g.score_b;
    // Compared against the team the slot actually shows — the resolved slot,
    // not only the raw column — so the green follows the winning team through
    // a rewire or a seed swap.
    const heldId = s?.registration_id ?? (side === 'a' ? g.team_a_registration_id : g.team_b_registration_id);
    const won = !!winnerId && winnerId === heldId;
    const shown = s?.team_name || name || s?.label || 'TBD';
    const isTeam = !!heldId;
    const inner = (
      <div
        className={`flex w-full items-end justify-between gap-2 border-b-2 border-foreground px-1 text-left ${won ? 'bg-success-subtle' : ''}`}
        style={{ height: ROW_H }}
        title={s?.unresolved_reason || undefined}
        data-bracket-team-row={side}
      >
        <span className={`truncate text-sm leading-[25px] ${won ? 'font-bold text-foreground' : isTeam ? 'text-foreground' : 'italic text-muted-foreground'}`}>
          {shown}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          <span className="text-sm font-bold tabular-nums">{score ?? ''}</span>
          {canEdit && <ChevronDown className="h-3 w-3 text-muted-foreground" />}
        </span>
      </div>
    );
    if (!canEdit) return inner;
    return (
      <SlotPicker g={g} side={side} disabled={saving} teams={teams} bracketGames={bracketGames} brackets={brackets} division={division} onSetSlot={onSetSlot}>
        <button type="button" data-testid="bracket-slot" {...stopDrag} className="block w-full rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {inner}
        </button>
      </SlotPicker>
    );
  };

  return (
    <div
      ref={canDrag ? setNodeRef : undefined}
      {...(canDrag ? listeners : {})}
      className={`${style ? 'absolute' : 'relative'} group ${canDrag ? 'cursor-grab' : ''} ${placement ? 'opacity-80 [&_[data-bracket-team-row]]:border-dashed' : ''} ${(moving || isDragging) ? 'opacity-40 ring-2 ring-primary' : ''}`}
      style={{ ...(style ?? {}), width: w ?? COL_W_FALLBACK, height: MATCHUP_H }}
      data-bracket-game={g.id}
      data-bracket-drag-surface={canDrag ? 'block' : undefined}
      data-bracket-placement={placement ? 'true' : undefined}
      data-matchup-height={MATCHUP_H}
      data-team-row-distance={MATCHUP_GAP}
    >
      {row('a')}
      {/* the vertical that joins the two team lines */}
      <div className="absolute border-r-2 border-foreground" style={{ right: 0, top: ROW_H, height: MATCHUP_GAP }} />
      {/* The details stay on the fixed joiner's midpoint in every round. */}
      <div
        className="absolute"
        style={{ top: MATCHUP_H / 2, right: 6, left: canEdit ? 76 : 4 }}
        data-game-detail-anchor
        {...stopDrag}
      >
        {renaming ? (
          <Input
            autoFocus
            className="h-6 text-[11px]"
            value={draft}
            disabled={saving}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); setRenaming(false); void onRename?.(g.id, draft); }
              if (e.key === 'Escape') { e.preventDefault(); setRenaming(false); }
            }}
            onBlur={() => setRenaming(false)}
          />
        ) : (
          <GameDetails g={g} eventId={eventId} courtName={courtName} preview={preview} />
        )}
        {/* The director's own line about this game — travels with the game. */}
        <InlineNote
          value={g.public_note}
          canEdit={canEdit && !!onSaveNote}
          limit={GAME_NOTE_LIMIT}
          editTitle="Edit this game's note"
          className="mt-0.5"
          onSave={(next) => onSaveNote?.(g.id, next)}
        />
      </div>
      {canEdit && !renaming && (
        <div
          className="absolute flex items-center gap-0.5 opacity-100 transition-opacity focus-within:opacity-100 group-hover:opacity-100 md:opacity-0"
          style={{ top: ROW_H + 1, left: 2 }}
        >
          {canMove && onKeyMove && (
            <MoveHandle
              gameId={g.id}
              label={g.game_number ? `Game ${g.game_number}` : (g.bracket_label || 'this game')}
              draggable={!!canDrag}
              attributes={attributes}
              onKeyMove={onKeyMove}
            />
          )}
          {canMove && moveOptions && onMoveToRound && onKeyMove && (
            <MoveToMenu
              g={g}
              label={g.game_number ? `Game ${g.game_number}` : (g.bracket_label || 'this game')}
              options={moveOptions(g.id)}
              onPick={(round) => onMoveToRound(g.id, round)}
              onNudge={(dir) => onKeyMove(g.id, dir)}
            />
          )}
          <Button
            size="icon" variant="ghost" className="h-5 w-5" title="Name this game"
            {...stopDrag}
            onClick={(e) => { e.stopPropagation(); setDraft(String(g.bracket_label ?? '')); setRenaming(true); }}
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-5 w-5" title="More" {...stopDrag}>
                <MoreHorizontal className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem disabled={!isPlaced} onSelect={() => onTakeOff?.(g)}>
                Take off the board
              </DropdownMenuItem>
              <DropdownMenuItem className="text-destructive" onSelect={() => onDelete?.(g)}>
                Delete game
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
      <div className="absolute inset-x-0" style={{ top: MATCHUP_GAP }}>{row('b')}</div>
    </div>
  );
};




/**
 * The visible grip. The drag itself now lives on the whole game block — a
 * 20px target was below every pointer guideline — but the grip stays as the
 * affordance, and it is where the arrow keys are handled, so moving a game
 * needs no "pick up" step for anyone not using a mouse.
 */
const MoveHandle: React.FC<{
  gameId: string;
  label: string;
  draggable: boolean;
  attributes?: any;
  onKeyMove: (gameId: string, dir: 'up' | 'down' | 'left' | 'right') => void;
}> = ({ gameId, label, draggable, attributes, onKeyMove }) => {
  return (
    <button
      type="button"
      {...(draggable ? attributes : {})}
      data-bracket-move-handle={gameId}
      title={draggable
        ? 'Drag this game, or use the arrow keys — or pick Move to…'
        : 'Use the arrow keys to move this game, or pick Move to…'}
      aria-label={`Move ${label}. Arrow keys move it up, down, or to another round.`}
      className="flex h-6 w-6 cursor-grab items-center justify-center rounded-sm text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onKeyDown={(e) => {
        const dir = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' }[e.key];
        if (!dir) return;
        e.preventDefault();
        e.stopPropagation();
        onKeyMove(gameId, dir as any);
      }}
    >
      <GripVertical className="h-3 w-3" />
    </button>
  );
};

/** One place a dragged game may be dropped: a round, and a position in it. */
const DropZone: React.FC<{
  round: number;
  index: number;
  box: { left: number; top: number; width: number; height: number };
  blocked: string | null;
  active: boolean;
}> = ({ round, index, box, blocked, active }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `bz:${round}:${index}`, disabled: !!blocked });
  if (!active) return null;
  return (
    <div
      ref={setNodeRef}
      data-bracket-drop-zone={`${round}:${index}`}
      data-bracket-drop-blocked={blocked ? 'true' : undefined}
      title={blocked ?? undefined}
      className={`absolute rounded-sm border-2 border-dashed transition-colors ${
        blocked
          ? 'cursor-not-allowed border-destructive/40 bg-destructive/5'
          : isOver
            ? 'border-primary bg-primary/15'
            : 'border-primary/40 bg-primary/5'
      }`}
      style={{ ...box, zIndex: 5 }}
    />
  );
};


const BracketView: React.FC<BracketViewProps> = ({
  eventId,
  canManage: canManageProp,
  versionId: versionIdProp,
  division: divisionProp,
  bracketId,
  bracketName,
  hideActions,
  preview,
  refreshToken,
  editable: editableProp,
  onChanged,
}) => {
  // Preview is what a parent sees: no management chrome, no editing, whatever
  // the page above asked for.
  const canManage = preview ? false : canManageProp;
  const editable = preview ? false : editableProp;
  const [loading, setLoading] = useState(true);
  const [versionId, setVersionId] = useState<string | null>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [courts, setCourts] = useState<Record<string, string>>({});
  const [division, setDivision] = useState<string | null>(null);
  const [divisions, setDivisions] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [seeds, setSeeds] = useState<any[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  // Editing support: courts each division is allowed, and the division's teams.
  const [editMeta, setEditMeta] = useState<any>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [swapSeedsOpen, setSwapSeedsOpen] = useState(false);
  const [slotData, setSlotData] = useState<any>(null);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  // Clicking a game on the canvas opens its edit panel — one game at a time.
  const takeOffBoard = (g: any) => void saveEdit(g.id, { scheduled_date: null, scheduled_start_time: null, court_id: null });


  // Adding one game by hand into this bracket.
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addRound, setAddRound] = useState<string>('new');
  const [addSrc, setAddSrc] = useState<Record<'a' | 'b', { type: string; ref: any }>>({
    a: { type: '', ref: {} }, b: { type: '', ref: {} },
  });
  const [addWhen, setAddWhen] = useState<{ date: string; time: string; court: string }>({
    date: '', time: '', court: '',
  });
  const [addError, setAddError] = useState('');
  // Naming a new round, and naming the one game being added.
  const [addRoundName, setAddRoundName] = useState('');
  const [addGameName, setAddGameName] = useState('');
  // Renaming a whole round from its column heading.
  const [renameRound, setRenameRound] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  const bracketCanvasRef = useRef<HTMLDivElement>(null);
  // Column width and gap are derived from the measured pane, so the bracket
  // reflows instead of overflowing. Preview always fits the whole bracket on
  // one screen; the editor keeps full-size hit targets unless asked.
  const [paneBox, setPaneBox] = useState({ width: 1258, height: 695 });
  const [fitOn, setFitOn] = useState(false);
  useEffect(() => {
    const el = bracketCanvasRef.current;
    if (!el) return;
    const pane = findBracketScrollPane(el);
    const measure = () => {
      const next = {
        width: pane.clientWidth || el.clientWidth || 1258,
        height: pane.clientHeight || el.clientHeight || 695,
      };
      setPaneBox((prev) => (
        prev.width === next.width && prev.height === next.height ? prev : next
      ));
    };
    measure();
    window.addEventListener('resize', measure);
    if (typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (pane !== el) ro.observe(pane);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [loading, bracketId]);
  // The director's own wording: one block under the bracket's name, and one
  // line against any round. Round notes are keyed on the round number, so
  // renaming a round never loses its note.
  const [description, setDescription] = useState<string>('');
  const [roundNotes, setRoundNotes] = useState<Record<number, string>>({});

  const loadNotes = useCallback(async () => {
    if (!bracketId) { setDescription(''); setRoundNotes({}); return; }
    // A failed read here must never take the bracket down — the board matters
    // more than the wording on it.
    try {
      const [{ data: b }, { data: notes }] = await Promise.all([
        (supabase as any).from('event_brackets').select('id, description').eq('id', bracketId).maybeSingle(),
        (supabase as any).from('bracket_round_notes').select('bracket_round, note').eq('bracket_id', bracketId),
      ]);
      setDescription(String(b?.description ?? ''));
      setRoundNotes(Object.fromEntries((notes ?? []).map((n: any) => [Number(n.bracket_round), String(n.note ?? '')])));
    } catch {
      setDescription(''); setRoundNotes({});
    }
  }, [bracketId]);

  useEffect(() => { void loadNotes(); }, [loadNotes, refreshToken]);

  const saveDescription = useCallback(async (next: string) => {
    if (!bracketId) return false;
    const { error } = await (supabase as any)
      .from('event_brackets').update({ description: next || null }).eq('id', bracketId);
    if (error) { toast.error('That description was not saved.'); return false; }
    setDescription(next);
    toast.success(next ? 'Description saved.' : 'Description cleared.');
    return true;
  }, [bracketId]);

  const saveRoundNote = useCallback(async (round: number, next: string) => {
    if (!bracketId) return false;
    const { error } = next
      ? await (supabase as any).from('bracket_round_notes')
          .upsert({ bracket_id: bracketId, bracket_round: round, note: next }, { onConflict: 'bracket_id,bracket_round' })
      : await (supabase as any).from('bracket_round_notes')
          .delete().eq('bracket_id', bracketId).eq('bracket_round', round);
    if (error) { toast.error('That note was not saved.'); return false; }
    setRoundNotes((prev) => {
      const copy = { ...prev };
      if (next) copy[round] = next; else delete copy[round];
      return copy;
    });
    toast.success(next ? 'Note saved.' : 'Note removed.');
    return true;
  }, [bracketId]);

  const loadBracket = useCallback(
    async (vId: string) => {
      const { data } = await supabase
        .from('v_bracket_games')
        .select('*')
        .eq('schedule_version_id', vId)
        .order('bracket_round', { ascending: true })
        .order('bracket_slot', { ascending: true });
      const list = data || [];
      setRows(list);
      const divs = Array.from(new Set(list.map((r: any) => r.division).filter(Boolean)));
      setDivisions(divs);
      setDivision((cur) => (cur && divs.includes(cur) ? cur : divs[0] ?? null));
    },
    [],
  );


  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    let v: any = versionIdProp ? { id: versionIdProp } : null;
    if (!v) {
      const { data: versions } = await supabase
        .from('schedule_versions')
        .select('id, status, published_at, created_at')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });
      v =
        (versions || []).find((x: any) => x.status === 'published') || (versions || [])[0] || null;
    }
    setVersionId(v?.id ?? null);

    const { data: courtRows } = await supabase
      .from('event_courts')
      .select('id, name')
      .eq('event_id', eventId);
    setCourts(Object.fromEntries((courtRows || []).map((c: any) => [c.id, c.name])));

    if (v?.id) await loadBracket(v.id);
    else setRows([]);
    setLoading(false);
  }, [eventId, loadBracket, versionIdProp]);

  useEffect(() => {
    load();
  }, [load]);

  const refetch = useCallback(() => {
    if (versionId) loadBracket(versionId);
  }, [versionId, loadBracket]);

  // Re-read when the owner regenerates the bracket.
  useEffect(() => {
    if (refreshToken === undefined) return;
    refetch();
  }, [refreshToken, refetch]);

  // Live refresh when bracket games are scored elsewhere (rle-score-game).
  useEffect(() => {
    if (!versionId) return;
    const channel = supabase
      .channel(`bracket-${versionId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'scheduled_games' },
        () => refetch(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [versionId, refetch]);

  // Courts each division may use, and the division's teams — the same payload
  // the bulk edit grid reads, so the dropdowns here offer exactly what it does.
  useEffect(() => {
    if (!editable || !eventId) return;
    let cancelled = false;
    (async () => {
      const { data: res } = await invokeEdgeFunction(MANUAL_FN, {
        body: { action: 'bulk_edit_data', event_id: eventId },
      });
      if (!cancelled && res?.ok) setEditMeta(res.data);
    })();
    return () => { cancelled = true; };
  }, [editable, eventId, refreshToken]);

  /**
   * One cell change, sent down the shared path: bulk_save previews it against
   * the same validation the grid and bulk edit use, and only then commits.
   * There is no second write path for bracket games.
   */
  const saveEdit = useCallback(
    async (gameId: string, patch: Record<string, any>) => {
      setSavingEdit(true);
      try {
        const edits = [{ game_id: gameId, ...patch }];
        const call = (extra: Record<string, unknown>) =>
          invokeEdgeFunction(MANUAL_FN, {
            body: { action: 'bulk_save', event_id: eventId, edits, ...extra },
          }) as Promise<any>;

        const { data: pre, error: preErr } = await call({ preview: true });
        if (preErr || !pre?.ok) {
          toast.error(preErr?.message || pre?.error?.message || 'Could not check that change.');
          return false;
        }
        const issues = pre.data?.issues ?? [];
        if (issues.length) {
          toast.error(issues.map((i: any) => i.message || String(i)).join(' · '));
          return false;
        }
        (pre.data?.warnings ?? []).forEach((w: any) => toast.warning(typeof w === 'string' ? w : w.message));

        const { data: res, error } = await call({ confirm: true });
        if (error || !res?.ok || !res.data?.applied) {
          const why = (res?.data?.issues ?? []).map((i: any) => i.message || String(i)).join(' · ');
          toast.error(why || error?.message || res?.error?.message || 'Nothing was saved.');
          return false;
        }
        if (!Number(res.data?.updated ?? 0)) {
          toast.error('The server accepted that but changed nothing — reload the bracket.');
          return false;
        }
        // "Saved." only after the server confirmed the write, and only in the
        // words of what it actually did: a game missing any of date, time or
        // court stays in the tray, and the card says so rather than implying
        // it was placed.
        const touchesPlacement = ['scheduled_date', 'scheduled_start_time', 'court_id']
          .some((k) => Object.prototype.hasOwnProperty.call(patch, k));
        const placed = !!(patch.scheduled_date && patch.scheduled_start_time && patch.court_id);
        if (touchesPlacement && !placed) {
          toast.success('Saved — this game stays unplaced until it has a date, a time and a court.');
        } else {
          toast.success('Saved.');
        }
        refetch();
        onChanged?.();
        return true;
      } finally {
        setSavingEdit(false);
      }
    },
    [eventId, refetch, onChanged],
  );


  const activeDivision = divisionProp !== undefined ? divisionProp : division;
  const visible = useMemo(
    () => rows.filter((r: any) =>
      (activeDivision ? r.division === activeDivision : true)
      && (bracketId ? r.bracket_id === bracketId : true)),
    [rows, activeDivision, bracketId],
  );
  const winners = visible.filter((r: any) => r.bracket_group !== 'consolation');
  const consolation = visible.filter((r: any) => r.bracket_group === 'consolation');
  const maxRound = winners.reduce((m: number, r: any) => Math.max(m, r.bracket_round || 1), 1);
  const roundsMap = useMemo(() => {
    const m = new Map<number, any[]>();
    winners.forEach((g: any) => {
      const arr = m.get(g.bracket_round) || [];
      arr.push(g);
      m.set(g.bracket_round, arr);
    });
    return Array.from(m.entries()).sort((a, b) => a[0] - b[0]);
  }, [winners]);

  // Every game of the round, whichever side of the bracket it sits on — the
  // 3rd-place game is in round 4 too, and a rename has to reckon with it.
  const roundGamesAll = useMemo(() => {
    const m = new Map<number, any[]>();
    visible.forEach((g: any) => {
      const r = Number(g.bracket_round) || 1;
      const arr = m.get(r) || [];
      arr.push(g);
      m.set(r, arr);
    });
    return m;
  }, [visible]);

  /** A round is called what its games are called. */
  const roundNameFor = useCallback(
    (round: number) => displayRoundName(roundGamesAll.get(round) ?? [], round),
    [roundGamesAll],
  );

  // Teams and courts the edit dropdowns may offer for the division on screen.
  const divisionTeams = useMemo(() => {
    const all = (editMeta?.teams ?? []) as any[];
    return all
      .filter((t) => (activeDivision ? t.division === activeDivision : true))
      .map((t) => ({ id: t.id, name: t.name }));
  }, [editMeta, activeDivision]);

  const divisionCourts = useMemo(() => {
    const all = (editMeta?.courts ?? []) as any[];
    const allowed = activeDivision ? editMeta?.allowed_courts?.[activeDivision] : null;
    const ids = Array.isArray(allowed) && allowed.length ? new Set(allowed) : null;
    return all.filter((c) => (ids ? ids.has(c.id) : true)).map((c) => ({ id: c.id, name: c.name }));
  }, [editMeta, activeDivision]);

  // Every slot of this division, resolved by the shared resolver: a real team
  // where it can be decided today, and the label that will decide it where it
  // cannot ("Pool A · 1st", "Winner of Semifinal #1").
  const loadSlots = useCallback(async () => {
    // The single place every surface gets its slot values from. It is never
    // gated on who is looking: a slot shows a team or its label whether the
    // page mounted this read-only, managed, or editable. Any new page that
    // mounts BracketView gets resolved slots for free.
    if (!eventId || !activeDivision) { setSlotData(null); return; }
    const { data } = await invokeEdgeFunction(MANUAL_FN, {
      body: {
        action: 'bracket_slots',
        event_id: eventId,
        division: activeDivision,
        schedule_version_id: versionId ?? undefined,
      },
    });
    if (data?.ok) {
      const d = data.data ?? {};
      setSlotData({
        ...d,
        byKey: Object.fromEntries((d.slots ?? []).map((s: any) => [`${s.game_id}:${s.side}`, s])),
      });
    }
  }, [eventId, activeDivision, versionId]);


  useEffect(() => { void loadSlots(); }, [loadSlots, refreshToken, rows]);


  // Teams actually placed in this bracket — the candidates for a seed exchange.
  const bracketTeams = useMemo(() => {
    const m = new Map<string, string>();
    visible.forEach((g: any) => {
      if (g.team_a_registration_id) m.set(g.team_a_registration_id, g.team_a_name || 'Team');
      if (g.team_b_registration_id) m.set(g.team_b_registration_id, g.team_b_name || 'Team');
    });
    return Array.from(m.entries()).map(([registration_id, team_name]) => ({ registration_id, team_name }));
  }, [visible]);

  const slotOf = useCallback(
    (gameId: string, side: 'a' | 'b') => slotData?.byKey?.[`${gameId}:${side}`] ?? null,
    [slotData],
  );

  // A bye is not a game, so it has no row. It still has to be visible in the
  // picture: a side of a later-round game that comes straight from a team or a
  // place — rather than from the winner of an earlier game — had a bye, and it
  // is drawn as a chip in the round it skipped.
  const byesByRound = useMemo(() => {
    const m = new Map<number, Array<{ key: string; label: string }>>();
    winners.forEach((g: any) => {
      const round = Number(g.bracket_round) || 1;
      if (round < 2) return;
      (['a', 'b'] as const).forEach((side) => {
        const slot = slotData?.byKey?.[`${g.id}:${side}`] ?? null;
        const type = slot?.source_type;
        if (!type || type === 'winner_of' || type === 'loser_of') return;
        const label = slot?.team_name || slot?.label || 'Bye';
        const arr = m.get(round - 1) || [];
        arr.push({ key: `${g.id}:${side}`, label });
        m.set(round - 1, arr);
      });
    });
    return m;
  }, [winners, slotData]);

  const setSlot = useCallback(
    async (gameId: string, side: 'a' | 'b', source_type: string, source_ref: any) => {
      setSavingEdit(true);
      try {
        const { data, error } = await invokeEdgeFunction(MANUAL_FN, {
          body: { action: 'set_bracket_slot', event_id: eventId, game_id: gameId, side, source_type, source_ref },
        });
        if (error || !data?.ok) {
          toast.error(error?.message || data?.error?.message || 'Could not set that slot.');
          return false;
        }
        toast.success(data.data?.message || 'Slot set.');
        // Hold the saved source on screen straight away. Waiting for the
        // re-read let the card fall back to "Pick a source" for a moment, so a
        // director watched their edit disappear the instant it succeeded.
        const saved = data.data?.slot ?? null;
        setSlotData((prev: any) => {
          if (!prev) return prev;
          const key = `${gameId}:${side}`;
          const merged = { ...(prev.byKey?.[key] ?? { game_id: gameId, side }), source_type, source_ref, ...(saved ?? {}) };
          return {
            ...prev,
            slots: (prev.slots ?? []).some((s: any) => s.game_id === gameId && s.side === side)
              ? prev.slots.map((s: any) => (s.game_id === gameId && s.side === side ? merged : s))
              : [...(prev.slots ?? []), merged],
            byKey: { ...(prev.byKey ?? {}), [key]: merged },
          };
        });
        refetch();
        loadSlots();
        onChanged?.();
        return true;
      } finally {
        setSavingEdit(false);
      }
    },
    [eventId, refetch, loadSlots, onChanged],
  );

  /** Deleting a game reads the real wiring — source_a_ref / source_b_ref — and
   *  refuses while another game feeds off this one, naming it. Compatibility
   *  pointers and the display label in bracket_feed_a are never consulted. */
  const deleteGame = useCallback(
    async (game: any) => {
      if (!game?.id) {
        toast.error('That game is no longer selected. Open Delete game again.');
        return false;
      }
      setSavingEdit(true);
      try {
        const { data, error } = await invokeEdgeFunction(MANUAL_FN, {
          body: { action: 'delete_game', event_id: eventId, game_id: game.id, confirm: true },
        });
        // The server answers a first, unconfirmed call with a question — that
        // is dialog text, never a result. A refusal is a refusal: red, past
        // tense, and it names what has to be cleared first.
        if (error || !data?.ok || data?.data?.deleted !== true) {
          toast.error(
            error?.message || data?.error?.message || 'Not deleted — nothing was removed.',
          );
          setPendingDelete(null);
          return false;
        }
        toast.success(data.data?.message || 'Game deleted.');
        setPendingDelete(null);
        refetch();
        loadSlots();
        onChanged?.();
        return true;

      } finally {
        setSavingEdit(false);
      }
    },
    [eventId, refetch, loadSlots, onChanged],
  );

  /** Name one game. Wording only — round, feeds, placement and number stay. */
  /** A line of the director's own text on one game. It lives on the game row,
   *  so it travels with a reschedule and goes when the game is deleted. */
  const saveGameNote = useCallback(
    async (gameId: string, note: string) => {
      const { error } = await (supabase as any)
        .from('scheduled_games').update({ public_note: note || null }).eq('id', gameId);
      if (error) { toast.error('That note was not saved.'); return false; }
      setRows((prev) => prev.map((r: any) => (r.id === gameId ? { ...r, public_note: note || null } : r)));
      toast.success(note ? 'Note saved.' : 'Note removed.');
      return true;
    },
    [],
  );

  const renameGame = useCallback(
    async (gameId: string, name: string) => {
      setSavingEdit(true);
      try {
        const { data, error } = await invokeEdgeFunction(MANUAL_FN, {
          body: { action: 'rename_game', event_id: eventId, game_id: gameId, name },
        });
        if (error || !data?.ok || data.data?.renamed !== true) {
          toast.error(error?.message || data?.error?.message || 'That name was not saved.');
          return false;
        }
        toast.success(data.data?.message || 'Name saved.');
        refetch();
        loadSlots();
        onChanged?.();
        return true;
      } finally {
        setSavingEdit(false);
      }
    },
    [eventId, refetch, loadSlots, onChanged],
  );

  /** Rename a whole round, after saying which games it touches. */
  const applyRenameRound = useCallback(
    async (round: number, name: string) => {
      if (!bracketId) return;
      setRenameBusy(true);
      try {
        const { data, error } = await invokeEdgeFunction(MANUAL_FN, {
          body: {
            action: 'rename_round',
            event_id: eventId,
            bracket_id: bracketId,
            bracket_round: round,
            name,
            confirm: true,
          },
        });
        if (error || !data?.ok || data.data?.renamed !== true) {
          toast.error(error?.message || data?.error?.message || 'That round was not renamed.');
          return;
        }
        toast.success(data.data?.message || 'Round renamed.');
        setRenameRound(null);
        refetch();
        loadSlots();
        onChanged?.();
      } finally {
        setRenameBusy(false);
      }
    },
    [bracketId, eventId, refetch, loadSlots, onChanged],
  );

  const cardProps = {
    eventId,
    editable: !!editable,
    teams: divisionTeams,
    courts: divisionCourts,
    saving: savingEdit,
    onSave: saveEdit,
    slotOf,
    onSetSlot: setSlot,
    onDelete: editable ? (game: any) => setPendingDelete(game) : undefined,
    onRename: editable ? renameGame : undefined,
    // A slot may feed from any game in the same division — a consolation
    // bracket runs on the losers of the main one — but never from another
    // division. The function refuses the rest as well.
    // Every option leads with its game number, in one ascending run, so no
    // two options can read the same and a director can pick by number.
    bracketGames: (slotData?.games ?? [])
      .map((x: any) => ({ ...x, label: gameName({ game_number: x.game_number, bracket_label: x.words ?? x.label }) }))
      .sort((a: any, b: any) => (a.game_number ?? 1e9) - (b.game_number ?? 1e9)),
    // The other brackets in this division, by name, so the menu can group its
    // cross-bracket options: "16u Winners — Loser of Game 34".
    divisionBrackets: (slotData?.brackets ?? []) as Array<{ id: string; name: string }>,
    pools: slotData?.pools ?? [],
    teamCount: slotData?.team_count ?? 0,

  };

  /**
   * Where every matchup sits comes from computeBracketLayout — a pure function
   * with its own tests, so the geometry can be proved without rendering. The
   * canvas draws every game in the bracket, selected by bracket_id.
   * bracket_group is styling only: a 3rd-place game belongs on the board.
   *
   * A bye has no game row, so it cannot be an engine input; it is drawn here
   * from the matchup's own slot centres.
   */
  const layout = useMemo(() => {
    const byId = new Map(visible.map((g: any) => [g.id, g]));
    const feederOf = (gameId: string, side: 'a' | 'b') => {
      const s = slotOf?.(gameId, side) ?? slotData?.byKey?.[`${gameId}:${side}`] ?? null;
      const t = s?.source_type;
      const id = t === 'winner_of' || t === 'loser_of' ? s?.source_ref?.game_id : null;
      return id && byId.has(id) ? String(id) : null;
    };
    // The render order the component already has: bracket_round, then
    // bracket_slot — the seeding position the generator wrote. Never
    // game_number, which is not seeding order.
    const order = visible.slice().sort((a: any, b: any) =>
      (Number(a.bracket_round) || 1) - (Number(b.bracket_round) || 1)
      || (a.bracket_slot ?? 0) - (b.bracket_slot ?? 0));
    const seen = new Map<number, number>();
    const games: BracketGameInput[] = order.map((g: any) => {
      const round = Number(g.bracket_round) || 1;
      const seedIndex = seen.get(round) ?? 0;
      seen.set(round, seedIndex + 1);
      return {
        id: String(g.id),
        gameNumber: Number(g.game_number) || 0,
        round,
        seedIndex,
        feederIds: (['a', 'b'] as const)
          .map((side) => feederOf(g.id, side))
          .filter((id): id is string => !!id),
      };
    });

    const engine = computeBracketLayout(games, { containerWidth: paneBox.width });
    const placed = new Map(engine.matchups.map((m) => [m.id, m]));

    const nodes = order
      .map((g: any) => ({ g, m: placed.get(String(g.id)) }))
      .filter((n: any) => !!n.m);

    const byes: any[] = [];
    for (const g of order) {
      const round = Number(g.bracket_round) || 1;
      const m = placed.get(String(g.id));
      if (!m || round <= 1) continue;
      for (const side of ['a', 'b'] as const) {
        if (feederOf(g.id, side)) continue;
        byes.push({
          key: `${g.id}:${side}`,
          gameId: g.id,
          side,
          x: Math.max(0, m.x - engine.gap - engine.colW),
          y: side === 'a' ? m.slotACy : m.slotBCy,
          width: engine.colW + engine.gap,
        });
      }
    }

    const contentTop = engine.matchups.length ? Math.min(...engine.matchups.map((m) => m.y)) : 0;
    const contentBottom = engine.matchups.length ? Math.max(...engine.matchups.map((m) => m.y + m.h)) : 0;
    return { ...engine, nodes, byes, contentTop, contentBottom };
  }, [visible, slotData, slotOf, paneBox.width]);

  // bracket_group is styling, never a filter: a placement game is drawn on the
  // board, lighter and with dashed feeds, below the championship path.
  const placementIds = useMemo(
    () => new Set(visible.filter((g: any) => g.bracket_group === 'consolation').map((g: any) => String(g.id))),
    [visible],
  );

  /**
   * A game with nothing in either slot — no team, no feed — cannot decide who
   * plays in it. The engine already marks the ones it could not position from
   * feeders; a first-round game holding real teams is not one of them.
   */
  const unwiredGames = useMemo(() => {
    const has = (gameId: string, side: 'a' | 'b') => {
      const s = slotOf?.(gameId, side) ?? null;
      return !!(s?.registration_id || s?.source_type);
    };
    return layout.matchups.filter((m: any) => {
      const g: any = visible.find((x: any) => String(x.id) === String(m.id));
      if (!g) return false;
      if (g.team_a_registration_id || g.team_b_registration_id) return false;
      return !has(g.id, 'a') && !has(g.id, 'b');
    });
  }, [layout, visible, slotOf]);

  /** The most likely bracket to wire from: the biggest other one, by name. */
  const wireFromName = useMemo(() => {
    const others = (slotData?.brackets ?? []).filter((b: any) => String(b.id) !== String(bracketId));
    return others.length === 1 ? others[0].name : null;
  }, [slotData, bracketId]);


  const fit = useMemo(() => {
    if (!preview && !fitOn) return 1;
    return fitToScreen(layout, {
      width: paneBox.width,
      height: Math.max(0, paneBox.height - ROUND_HEADER_H),
    });
  }, [preview, fitOn, layout, paneBox.width, paneBox.height]);

  const fittedWidth = layout.width * fit;
  const fittedHeight = ROUND_HEADER_H + layout.height * fit;

  // ---------------------------------------------------------------- moving
  // A game added by hand lands at the end of a round. This is how it gets
  // where it belongs: a drag writes bracket_round and bracket_slot and nothing
  // else, and the canvas recomputes every coordinate from those, so the
  // connectors keep meeting the games.
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [moveBusy, setMoveBusy] = useState(false);
  const [announce, setAnnounce] = useState('');
  // "Move to…" and the arrow keys are a choice from a short list, so they work
  // at any zoom. Scaled by Fit to screen a pointer position no longer matches
  // the canvas, so the drag itself is offered at actual size only.
  const canMoveGames = !!editable && !preview && !!bracketId && !moveBusy;
  const canDragGames = canMoveGames && fit === 1;

  /** The games of one round, top to bottom, as the canvas draws them. */
  const roundOrder = useCallback(
    (round: number) => layout.nodes
      .filter((n: any) => (Number(n.g.bracket_round) || 1) === round)
      .slice()
      .sort((a: any, b: any) => a.m.y - b.m.y),
    [layout],
  );

  const moveGame = useCallback(async (gameId: string, toRound: number, toIndex: number) => {
    const plan = planBracketMove(visible, { gameId, toRound, toIndex });
    if (!plan.ok) {
      // Dropping a game back where it started is not an error worth a toast.
      if (!/is already in round/i.test(plan.message)) toast.error(plan.message);
      setAnnounce(plan.message);
      return false;
    }
    setMoveBusy(true);
    try {
      const { data, error } = await invokeEdgeFunction(MANUAL_FN, {
        body: { action: 'bracket_move_game', event_id: eventId, game_id: gameId, to_round: toRound, to_index: toIndex },
      }) as any;
      if (error || !data?.ok) {
        toast.error(data?.error?.message || error?.message || 'That game was not moved.');
        return false;
      }
      toast.success(data.data?.message ?? 'Moved.');
      setAnnounce(data.data?.message ?? 'Moved.');
      refetch();
      onChanged?.();
      return true;
    } finally {
      setMoveBusy(false);
    }
  }, [visible, eventId, refetch, onChanged]);

  /** Arrow keys: up and down reorder inside a round, left and right change it. */
  const keyMove = useCallback((gameId: string, dir: 'up' | 'down' | 'left' | 'right') => {
    const g: any = visible.find((x: any) => String(x.id) === String(gameId));
    if (!g) return;
    const round = Number(g.bracket_round) || 1;
    const here = roundOrder(round);
    const at = here.findIndex((n: any) => String(n.g.id) === String(gameId));
    if (dir === 'up' || dir === 'down') {
      const next = dir === 'up' ? at - 1 : at + 1;
      if (next < 0 || next >= here.length) { setAnnounce(endOfRoundWords(g, dir)); return; }
      void moveGame(gameId, round, next);
      return;
    }
    // Left and right step between the columns that exist, and right off the
    // end starts a new round — the same set of destinations the menu lists and
    // the drag draws, so no path can reach somewhere the others cannot.
    const rounds = Array.from(new Set<number>(visible.map((x: any) => Number(x.bracket_round) || 1))).sort((a, b) => a - b);
    const toRound = dir === 'left'
      ? [...rounds].reverse().find((r) => r < round)
      : (rounds.find((r) => r > round) ?? Math.max(...rounds) + 1);
    if (!toRound) { setAnnounce(`${gameWords(g)} is already in the first round.`); return; }
    const blocked = moveBlocker(visible, gameId, toRound, roundOrder(toRound).length);
    if (blocked) { toast.error(blocked); setAnnounce(blocked); return; }
    void moveGame(gameId, toRound, roundOrder(toRound).length);
  }, [visible, roundOrder, moveGame]);

  /**
   * "Move to…": the short list of where this game may go. Every round in the
   * bracket is on it, the impossible ones disabled and carrying the very
   * sentence the drag and the arrow keys refuse with — one function, three
   * callers, so an operator is taught the rule instead of guessing at it.
   */
  const moveOptionsFor = useCallback((gameId: string) => {
    const g: any = visible.find((x: any) => String(x.id) === String(gameId));
    const current = Number(g?.bracket_round) || 1;
    const here = roundOrder(current);
    const at = here.findIndex((n: any) => String(n.g.id) === String(gameId));
    const existing = Array.from(
      new Set<number>([
        ...visible.map((x: any) => Number(x.bracket_round) || 1),
        current,
      ]),
    ).sort((a, b) => a - b);
    // A game may also start a round of its own — that is Mike's case: a new
    // round, a game that landed in the wrong column. The arrow keys have always
    // been able to do it, so the menu offers it too, or the two paths teach
    // different rules. The round it leaves empties and its column disappears,
    // because the canvas draws only the rounds that hold games.
    const nextRound = Math.max(...existing) + 1;
    const rounds = [...existing, nextRound];
    return {
      current,
      rounds: rounds.map((r) => ({
        round: r,
        label: r === nextRound
          ? `Round ${r} — new round`
          : roundNameFor(r) === `Round ${r}` ? `Round ${r}` : `Round ${r} — ${roundNameFor(r)}`,
        current: r === current,
        reason: r === current ? null : moveBlocker(visible, gameId, r, roundOrder(r).length),
      })),
      up: {
        disabled: at <= 0 || !!moveBlocker(visible, gameId, current, at - 1),
        reason: at <= 0
          ? (g ? endOfRoundWords(g, 'up') : null)
          : moveBlocker(visible, gameId, current, at - 1),
      },
      down: {
        disabled: at < 0 || at >= here.length - 1 || !!moveBlocker(visible, gameId, current, at + 1),
        reason: at >= here.length - 1
          ? (g ? endOfRoundWords(g, 'down') : null)
          : moveBlocker(visible, gameId, current, at + 1),
      },
    };
  }, [visible, roundOrder, roundNameFor]);

  /** Chosen from the menu: the game joins the end of that round. */
  const moveToRound = useCallback((gameId: string, round: number) => {
    const at = roundOrder(round).length;
    const blocked = moveBlocker(visible, gameId, round, at);
    if (blocked) { toast.error(blocked); setAnnounce(blocked); return; }
    void moveGame(gameId, round, at);
  }, [visible, roundOrder, moveGame]);

  /**
   * Where the dragged game may be dropped. One strip per position in every
   * round, plus one for a new round on the right. A round its feeds forbid is
   * drawn as refused before he lets go, not after.
   */
  const dropZones = useMemo(() => {
    if (!draggingId) return [];
    const cols = layout.columns ?? [];
    const gap = layout.gap ?? 40;
    const colW = layout.colW ?? 220;
    const lastX = cols.length ? Math.max(...cols.map((c: any) => c.x + c.w)) : 0;
    const targets = [
      ...cols.map((c: any) => ({ round: c.round, x: c.x, w: c.w })),
      { round: (cols.length ? Math.max(...cols.map((c: any) => c.round)) : 0) + 1, x: lastX + gap, w: colW },
    ];
    const out: any[] = [];
    for (const t of targets) {
      const here = roundOrder(t.round).filter((n: any) => String(n.g.id) !== String(draggingId));
      const centres = here.map((n: any) => n.m.y + n.m.h / 2);
      for (let i = 0; i <= here.length; i++) {
        const top = i === 0 ? 0 : centres[i - 1];
        const bottom = i === here.length ? layout.height : centres[i];
        out.push({
          key: `${t.round}:${i}`,
          round: t.round,
          index: i,
          // Per position, not per column: a round may be fine while one place
          // inside it would cross the connectors.
          blocked: moveBlocker(visible, draggingId, t.round, i),
          box: { left: t.x, top, width: t.w, height: Math.max(8, bottom - top) },
        });
      }
    }
    return out;
  }, [draggingId, layout, roundOrder, visible]);

  const dragSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));



  const existingRounds = useMemo(
    () => Array.from(new Set(visible.map((g: any) => Number(g.bracket_round) || 1))).sort((a, b) => a - b),
    [visible],
  );

  /** A hand-added game is a real game: same function, same validation. */
  const submitAddGame = async () => {
    if (!activeDivision || !bracketId) return;
    setAddBusy(true); setAddError('');
    const clean = (s: { type: string; ref: any }) => (s.type ? { type: s.type, ref: s.ref } : null);
    const { data, error } = await invokeEdgeFunction(MANUAL_FN, {
      body: {
        action: 'add_game',
        event_id: eventId,
        division: activeDivision,
        game_type: 'bracket',
        bracket_id: bracketId,
        bracket_round: addRound === 'new' ? undefined : Number(addRound),
        // Blank is fine on both: the round falls back to "Round N", and the
        // game takes the name of the round it lands in.
        round_label: addRound === 'new' ? addRoundName.trim() || undefined : undefined,
        game_name: addGameName.trim() || undefined,
        source_a: clean(addSrc.a),
        source_b: clean(addSrc.b),
        scheduled_date: addWhen.date || null,
        scheduled_start_time: addWhen.time || null,
        court_id: addWhen.court || null,
        confirm: true,
      },
    });
    setAddBusy(false);
    if (error || !data?.ok || data.data?.created === false) {
      const issues = (data?.data?.issues ?? []).map((i: any) => i.message || String(i)).join(' · ');
      setAddError(issues || error?.message || data?.error?.message || 'That game could not be added.');
      return;
    }
    toast.success(data.data?.message || 'Game added.');
    setAddOpen(false);
    setAddSrc({ a: { type: '', ref: {} }, b: { type: '', ref: {} } });
    setAddWhen({ date: '', time: '', court: '' });
    setAddRound('new');
    setAddRoundName('');
    setAddGameName('');
    refetch();
    loadSlots();
    onChanged?.();
  };

  const addSlotPicker = (side: 'a' | 'b') => {
    const cur = addSrc[side];
    const set = (patch: any) => setAddSrc((m) => ({ ...m, [side]: { ...m[side], ...patch } }));
    return (
      <div className="space-y-1">
        <div className="text-xs font-medium text-muted-foreground">
          {side === 'a' ? 'Top slot' : 'Bottom slot'}
        </div>
        <div className="flex items-center gap-1.5">
          <Select value={cur.type} onValueChange={(v) => set({ type: v, ref: {} })}>
            <SelectTrigger className="h-8 w-[124px] text-xs"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              {Object.entries(SOURCE_LABELS).map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {cur.type === 'team' && (
            <Select value={cur.ref?.registration_id ?? ''} onValueChange={(v) => set({ ref: { registration_id: v } })}>
              <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue placeholder="Pick a team" /></SelectTrigger>
              <SelectContent>
                {divisionTeams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {(cur.type === 'winner_of' || cur.type === 'loser_of') && (
            <Select value={cur.ref?.game_id ?? ''} onValueChange={(v) => set({ ref: { game_id: v } })}>
              <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue placeholder="Pick a game" /></SelectTrigger>
              <SelectContent>
                {cardProps.bracketGames.map((x: any) => (
                  <SelectItem key={x.id} value={x.id}>{x.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {cur.type === 'division_place' && (
            <Select value={cur.ref?.place ? String(cur.ref.place) : ''} onValueChange={(v) => set({ ref: { place: Number(v) } })}>
              <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue placeholder="Pick a place" /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: Math.max(2, cardProps.teamCount || 2) }, (_, i) => i + 1).map((p) => (
                  <SelectItem key={p} value={String(p)}>{ordinal(p)} in the division</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {cur.type === 'pool_place' && (
            <div className="flex flex-1 items-center gap-1">
              <Select value={cur.ref?.pool ?? ''} onValueChange={(v) => set({ ref: { ...cur.ref, pool: v } })}>
                <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue placeholder="Pool" /></SelectTrigger>
                <SelectContent>
                  {(cardProps.pools ?? []).map((p: string) => (
                    <SelectItem key={p} value={p}>{/^pool\b/i.test(p) ? p : `Pool ${p}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={cur.ref?.place ? String(cur.ref.place) : ''}
                onValueChange={(v) => set({ ref: { ...cur.ref, place: Number(v) } })}
              >
                <SelectTrigger className="h-8 w-[80px] text-xs"><SelectValue placeholder="Place" /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6].map((p) => (
                    <SelectItem key={p} value={String(p)}>{ordinal(p)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
    );
  };



  const openManual = async () => {
    const { data } = await supabase.rpc('get_event_registered_teams', { _event_id: eventId });
    const list = (data || [])
      .filter((t: any) => (division ? t.division === division || t.age_group === division : true))
      .map((t: any) => ({
        registration_id: t.id,
        name: t.display_name_override || t.team_display_name || t.team_canonical_name || 'Team',
      }));
    setSeeds(list);
    setManualOpen(true);
  };

  const generate = async (source: 'pools' | 'manual') => {
    if (!versionId) return;
    setGenerating(true);
    const body: any = { schedule_version_id: versionId, division, source, third_place: true };
    if (source === 'manual') body.seeds = seeds.map((s) => ({ registration_id: s.registration_id }));
    const { data, error } = await invokeEdgeFunction(GENERATE_FN, { body });
    setGenerating(false);
    if (error || data?.ok === false) {
      toast.error(error?.message || data?.error?.message || 'Could not generate the bracket');
      return;
    }
    toast.success('Bracket generated');
    setConfirmOpen(false);
    setManualOpen(false);
    refetch();
  };

  const onDrop = (index: number) => {
    if (dragIndex === null || dragIndex === index) return;
    setSeeds((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragIndex(null);
  };

  if (loading) {
    return (
      <div className="flex min-h-[30vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {divisionProp === undefined &&
            divisions.map((d) => (
              <Button
                key={d}
                size="sm"
                variant={d === division ? 'default' : 'outline'}
                onClick={() => setDivision(d)}
              >
                {d}
              </Button>
            ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {editable && activeDivision && bracketId && (
            <Button size="sm" variant="outline" onClick={() => { setAddError(''); setAddOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              Add game
            </Button>
          )}
          {editable && activeDivision && bracketTeams.length > 1 && (
            <Button size="sm" variant="outline" onClick={() => setSwapSeedsOpen(true)}>
              <ArrowLeftRight className="mr-2 h-4 w-4" />
              Swap seeds in this bracket
            </Button>
          )}
          {canManage && !hideActions && versionId && (
            <>
              <Button size="sm" variant="outline" onClick={openManual}>
                <ListOrdered className="mr-2 h-4 w-4" />
                Manual seeds
              </Button>
              <Button size="sm" onClick={() => setConfirmOpen(true)}>
                <Wand2 className="mr-2 h-4 w-4" />
                Generate bracket
              </Button>
            </>
          )}
        </div>
      </div>

      {editable && activeDivision && (
        <SwapSeedsDialog
          open={swapSeedsOpen}
          onOpenChange={setSwapSeedsOpen}
          eventId={eventId}
          division={activeDivision}
          teams={bracketTeams}
          onChanged={() => { refetch(); onChanged?.(); }}
        />
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {pendingDelete ? gameName(pendingDelete) : 'this bracket game'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This removes the game from the bracket. If another game takes its winner or
              loser, the delete is refused and you will be told which game depends on it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingEdit}>Keep it</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={savingEdit}
              onClick={(e) => { e.preventDefault(); void deleteGame(pendingDelete); }}
            >
              {savingEdit ? 'Deleting…' : 'Delete game'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>



      <Dialog open={addOpen} onOpenChange={(o) => !o && setAddOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add a game to {bracketName || 'this bracket'}</DialogTitle>
            <DialogDescription>
              It becomes a real game in this bracket — on the grid, the print pack and the public
              bracket. Court and time are optional; leave them blank and place it later.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* A round is offered by the name its games carry — Quarterfinal,
                not "Round 2" — because that is what is written on the board. */}
            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground">Round</div>
              <Select value={addRound} onValueChange={setAddRound}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {existingRounds.map((r) => (
                    <SelectItem key={r} value={String(r)}>{roundNameFor(r)}</SelectItem>
                  ))}
                  <SelectItem value="new">New round</SelectItem>
                </SelectContent>
              </Select>
              {addRound === 'new' && (
                <Input
                  className="h-8 text-xs"
                  placeholder="Consolation, Loser bracket, Placement"
                  value={addRoundName}
                  onChange={(e) => setAddRoundName(e.target.value)}
                />
              )}
              {addRound === 'new' && !addRoundName.trim() && (
                <p className="text-[11px] text-muted-foreground">
                  Leave it blank and the round is Round {existingRounds.length ? Math.max(...existingRounds) + 1 : 1}.
                </p>
              )}
            </div>

            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground">Name this game (optional)</div>
              <Input
                className="h-8 text-xs"
                placeholder={addRound === 'new'
                  ? (addRoundName.trim() || 'Takes the round’s name')
                  : roundNameFor(Number(addRound))}
                value={addGameName}
                onChange={(e) => setAddGameName(e.target.value)}
              />
            </div>

            {addSlotPicker('a')}
            {addSlotPicker('b')}

            <div className="flex items-center gap-1.5">
              <Input type="date" className="h-8 flex-1 text-xs" value={addWhen.date}
                onChange={(e) => setAddWhen((w) => ({ ...w, date: e.target.value }))} />
              <Input type="time" className="h-8 w-[104px] text-xs" value={addWhen.time}
                onChange={(e) => setAddWhen((w) => ({ ...w, time: e.target.value }))} />
            </div>
            <Select value={addWhen.court || TBD}
              onValueChange={(v) => setAddWhen((w) => ({ ...w, court: v === TBD ? '' : v }))}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="No court" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={TBD}>No court</SelectItem>
                {divisionCourts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>

            {addError && <p className="text-sm text-destructive">{addError}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={addBusy}>Cancel</Button>
            <Button onClick={() => void submitAddGame()} disabled={addBusy}>
              {addBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add game
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Renaming a round is wording only — it says what it will do to every
          game in the round before it does any of it. */}
      <Dialog open={renameRound !== null} onOpenChange={(o) => !o && setRenameRound(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Rename {renameRound !== null ? roundNameFor(renameRound) : 'this round'}
            </DialogTitle>
            <DialogDescription>
              This changes wording only. No game moves: the round, the feeds, the dates, the
              courts and the game numbers all stay exactly as they are.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              autoFocus
              placeholder="Consolation, Loser bracket, Championship"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
            />
            {renameRound !== null && (
              <p className="text-sm text-muted-foreground">
                {renamePlanSentence(
                  planRoundRename(roundGamesAll.get(renameRound) ?? [], renameRound, renameValue),
                )}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameRound(null)} disabled={renameBusy}>
              Cancel
            </Button>
            <Button
              disabled={renameBusy}
              onClick={() => renameRound !== null && void applyRenameRound(renameRound, renameValue)}
            >
              {renameBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Rename round
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {visible.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Trophy className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-1 text-lg font-semibold">No bracket yet</h3>
            <p className="text-muted-foreground">
              {canManage
                ? 'Generate a bracket from pool results, or set manual seeds.'
                : 'The bracket will appear once it has been generated.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div ref={bracketCanvasRef} className="relative overflow-x-auto pb-2" data-bracket-canvas>
          {/* The director's own words about this bracket, under its name. */}
          {bracketId && (description || (editable && !preview)) && (
            <div className="group mb-3 max-w-2xl" data-bracket-description>
              <InlineNote
                value={description}
                canEdit={!!editable && !preview}
                limit={BRACKET_DESCRIPTION_LIMIT}
                multiline
                addLabel="+ Add a description"
                editTitle="Edit this bracket's description"
                onSave={saveDescription}
              />
            </div>
          )}
          {/* Say it plainly when the bracket cannot decide who plays. */}
          {!preview && unwiredGames.length > 0 && (
            <div
              className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-foreground"
              data-bracket-unwired-banner
            >
              {unwiredGames.length} of {layout.matchups.length} game
              {layout.matchups.length === 1 ? '' : 's'} have nothing feeding them.{' '}
              {wireFromName ? `Wire them from the ${wireFromName} bracket, ` : 'Wire them from another bracket, '}
              or pick teams for each slot.
            </div>
          )}

          {/* Fit is the whole bracket on one screen. Preview always fits; the
              editor keeps full-size targets until a director asks otherwise. */}
          {!preview && layout.matchups.length > 0 && (
            <div className="mb-2">
              <Button size="sm" variant="outline" onClick={() => setFitOn((v) => !v)} data-bracket-fit-toggle>
                {fitOn ? 'Actual size' : 'Fit to screen'}
              </Button>
            </div>
          )}
          <DndContext
            sensors={dragSensors}
            collisionDetection={pointerWithin}
            autoScroll={{ threshold: { x: 0.15, y: 0.2 }, acceleration: 14 }}
            onDragStart={(e: any) => setDraggingId(String(e.active.id).replace(/^bg:/, ''))}
            onDragCancel={() => setDraggingId(null)}
            onDragEnd={(e: any) => {
              const gameId = String(e.active?.id ?? '').replace(/^bg:/, '');
              const over = e.over ? String(e.over.id) : '';
              setDraggingId(null);
              const m = over.match(/^bz:(\d+):(\d+)$/);
              if (!gameId || !m) return;
              void moveGame(gameId, Number(m[1]), Number(m[2]));
            }}
          >
          <div
            style={{ width: fittedWidth, height: fittedHeight }}
            data-bracket-fit={fit}
            data-bracket-pane-width={paneBox.width}
            data-bracket-pane-height={paneBox.height}
            data-bracket-fit-height={Math.max(0, paneBox.height - ROUND_HEADER_H)}
            data-bracket-fitted-height={fittedHeight}
          >
            <div
              style={{
                width: layout.width,
                transform: fit < 1 ? `scale(${fit})` : undefined,
                transformOrigin: 'top left',
              }}
            >
              {/* Round headings sit over their column, driven by the layout. */}
              <div className="relative" style={{ width: layout.width, height: ROUND_HEADER_H }}>
                {layout.columns.map((c: any) => (
                  <div key={c.round} className="group absolute" style={{ left: c.x, width: c.w }}>
                    <h3 className="flex items-center gap-1 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                      <span className="truncate">{roundNameFor(c.round)}</span>
                      {editable && bracketId && (
                        <Button
                          size="icon" variant="ghost" className="h-6 w-6 shrink-0"
                          title="Rename this round"
                          onClick={() => { setRenameValue(roundNameFor(c.round)); setRenameRound(c.round); }}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      )}
                    </h3>
                    {bracketId && (
                      <InlineNote
                        value={roundNotes[c.round]}
                        canEdit={!!editable && !preview}
                        limit={ROUND_NOTE_LIMIT}
                        editTitle="Edit this round's note"
                        onSave={(next) => saveRoundNote(c.round, next)}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* The bracket itself: every line is a border on a positioned div. */}
              <div
                className="relative"
                style={{ width: layout.width, height: layout.height }}
                data-bracket-lines
                data-bracket-content-top={layout.contentTop}
                data-bracket-content-bottom={layout.contentBottom}
              >
                {layout.connectors.map((c: any, index: number) => (
                  <div
                    key={`${c.toGameId}-${index}`}
                    data-bracket-connector
                    data-bracket-connector-part={c.orientation === 'h' ? 'horizontal' : 'vertical'}
                    data-bracket-target={c.toGameId}
                    data-bracket-spans={c.spansColumns ? 'true' : undefined}
                    className={`absolute ${placementIds.has(c.toGameId) ? 'border-foreground/50 border-dashed' : 'border-foreground'}`}
                    style={{
                      left: c.x,
                      top: c.y,
                      width: c.orientation === 'h' ? c.length : 0,
                      height: c.orientation === 'v' ? c.length : 0,
                      borderTopWidth: c.orientation === 'h' ? 2 : 0,
                      borderLeftWidth: c.orientation === 'v' ? 2 : 0,
                    }}
                  />
                ))}
                {layout.byes.map((b: any) => (
                  <div
                    key={`bye-${b.key}`}
                    data-bracket-bye-target={b.gameId}
                    data-bracket-bye-side={b.side}
                    className="absolute border-t-2 border-dashed border-foreground/60"
                    style={{ left: b.x, top: b.y, width: b.width }}
                  >
                    <span className="absolute bottom-0.5 left-1 text-[11px] italic text-muted-foreground">Bye</span>
                  </div>
                ))}
                {layout.nodes.map((n: any) => (
                  <Matchup
                    key={n.g.id}
                    g={n.g}
                    eventId={eventId}
                    courtName={courts[n.g.court_id]}
                    slotOf={slotOf}
                    style={{ left: n.m.x, top: n.m.y }}
                    w={n.m.w}
                    placement={n.g.bracket_group === 'consolation'}
                    editable={!!editable}
                    preview={!!preview}
                    saving={savingEdit}
                    teams={divisionTeams}
                    bracketGames={cardProps.bracketGames}
                    brackets={cardProps.divisionBrackets}
                    division={activeDivision}

                    onSetSlot={setSlot}
                    onRename={renameGame}
                    onDelete={(game) => setPendingDelete(game)}
                    onTakeOff={takeOffBoard}
                    onSaveNote={saveGameNote}
                    canMove={canMoveGames}
                    canDrag={canDragGames}
                    moveOptions={moveOptionsFor}
                    onMoveToRound={moveToRound}
                    onKeyMove={keyMove}
                    moving={draggingId === String(n.g.id)}
                  />
                ))}
                {dropZones.map((z: any) => (
                  <DropZone key={z.key} round={z.round} index={z.index} box={z.box} blocked={z.blocked} active />
                ))}
              </div>
            </div>
          </div>
          </DndContext>
          {/* What just happened, for a screen reader. */}
          <div className="sr-only" role="status" aria-live="polite" data-bracket-announce>{announce}</div>

        </div>

      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Generate bracket from pool results?</AlertDialogTitle>
            <AlertDialogDescription>
              This replaces any existing bracket{division ? ` for ${division}` : ''}, including
              scheduled times and scores on those bracket games. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={generating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                generate('pools');
              }}
              disabled={generating}
            >
              {generating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Replace bracket
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manual seeding</DialogTitle>
            <DialogDescription>
              Drag teams into seed order (top = 1 seed). Generating replaces any existing bracket
              {division ? ` for ${division}` : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] space-y-1 overflow-y-auto">
            {seeds.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No registered teams found for this division.
              </p>
            )}
            {seeds.map((s, i) => (
              <div
                key={s.registration_id}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(i)}
                className="flex cursor-grab items-center gap-2 rounded border bg-card px-3 py-2"
              >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
                <span className="w-6 text-xs font-semibold text-muted-foreground">{i + 1}</span>
                <span className="truncate text-sm">{s.name}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)} disabled={generating}>
              Cancel
            </Button>
            <Button onClick={() => generate('manual')} disabled={generating || seeds.length < 2}>
              {generating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Generate from seeds
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>

  );
};

export default BracketView;
